/**
 * Hero Sleeka — karuzela z prawdziwą pętlą.
 *
 * ── Dlaczego bez klonów ───────────────────────────────────────────────────
 * Każdy slajd dostaje własny transform. Pozycja `pos` liczona jest w
 * jednostkach slajdu i może rosnąć bez końca; przesunięcie slajdu `i` to
 *
 *     d = i - pos,  sprowadzone do przedziału [-N/2, N/2]
 *
 * czyli slajd zawsze ustawia się po tej stronie, z której jest bliżej.
 * Skok o całą długość toru wypada wtedy zawsze poza kadrem: przy trzech
 * slajdach granica leży 1.5 skoku od środka (1.065 szerokości okna), a slajd
 * kończy się na 0.849 — z zapasem 0.216. Dzięki temu przeskok jest fizycznie
 * niewidoczny i nie trzeba go niczym maskować.
 *
 * Stąd: zero klonów, zero przestawiania DOM-u, zero reflow. Na klatkę
 * przypada tyle zapisów `--sleek-hero-x`, ile jest slajdów, i nic poza tym.
 *
 * ── Dlaczego nie scroll-snap ──────────────────────────────────────────────
 * Pętla na kontenerze przewijanym wymaga albo klonów, albo przestawiania
 * slajdów w DOM-ie. Oba są widoczne w układzie z wystającymi sąsiadami,
 * bo sąsiad, którym się manipuluje, jest na ekranie.
 *
 * Geometria i wygląd: assets/sleek-hero.css + snippets/sleek-tokens.liquid.
 */

/*
 * UWAGA: caly plik siedzi w domknieciu.
 *
 * Arkusze skorki laduja sie jako ZWYKLE skrypty (`defer`, bez `type="module"`),
 * wiec ich zakres najwyzszego poziomu jest WSPOLNY. Dwa pliki deklarujace
 * `const reducedMotion` wywalaja drugi z nich w calosci:
 *
 *     Uncaught SyntaxError: Identifier 'reducedMotion' has already been declared
 *
 * Plik nie wykonuje sie wtedy WCALE — nie ma bledu w srodku, po prostu nie ma
 * komponentu. Tak zniknela kaskada wejscia siatki, gdy `sleek-reveal.js` stanal
 * na stronie glownej obok `sleek-hero.js`.
 */

(function () {
  /** Poniżej tej różnicy pozycji uznajemy ruch za zakończony (w slajdach). */
  const SETTLED = 0.001;

  /** Prędkość rzutu (px/ms), od której gest liczy się jako przerzucenie slajdu. */
  const FLICK_VELOCITY = 0.45;

  /** Najdłuższy krok całkowania — chroni przed skokiem po zablokowanej karcie. */
  const MAX_FRAME_MS = 64;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  class SleekHero extends HTMLElement {
    /** @type {HTMLElement[]} */ #slides = [];
    /** @type {HTMLElement[]} */ #dots = [];
    #pos = 0;
    #target = 0;
    #stride = 0;
    /* Stale skorki czytane RAZ przy pierwszym pomiarze — patrz `#measure`.
       Wartosci domyslne obowiazuja, dopoki `#stylesRead` nie jest prawda,
       wiec awaria odczytu zostawia dzialajaca karuzele zamiast zerowych
       progow. */
    #gap = 0;
    #tau = 110;
    #threshold = 8;
    #stylesRead = false;
    #frame = 0;
    #lastFrame = 0;
    #timer = 0;
    #active = -1;
    #loopable = true;
    #drag = null;
    #suppressClick = false;
    /** @type {ResizeObserver | null} */ #resize = null;

    connectedCallback() {
      this.#slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('slideshow-slide')]);
      this.#dots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.sleek-hero__dot')]);

      // Jeden slajd to nie karuzela — zostaje statycznym banerem.
      if (this.#slides.length < 2) {
        this.setAttribute('ready', '');
        return;
      }

      // Szerokość slajdu zmienia się z oknem, a od niej zależy skok toru.
      // Pierwsze wywołanie przychodzi od razu po `observe` i dopiero ono ma
      // pewny pomiar — przy starcie `offsetWidth` bywa jeszcze zerowe, a wtedy
      // slajdy leżałyby na sobie. Stąd `ready` zapala się właśnie tutaj.
      this.#resize = new ResizeObserver(() => {
        this.#measure();
        this.#paint();
        this.setAttribute('ready', '');
      });
      this.#resize.observe(this);

      this.addEventListener('pointerdown', this.#onPointerDown);
      this.addEventListener('click', this.#onClick, { capture: true });
      this.#dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      document.addEventListener('shopify:block:select', this.#onBlockSelect);

      this.#restartAutoplay();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      clearTimeout(this.#timer);
      this.#resize?.disconnect();
      this.#resize = null;
      this.#endDrag();
      document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    }

    /** Czas jednego slajdu w ms; 0 gdy autoplay wyłączony. */
    get #delay() {
      return Number(this.getAttribute('autoplay')) * 1000 || 0;
    }

    /** Przewija na slajd o podanym numerze, najkrótszą drogą przez pętlę. */
    goTo(index) {
      if (this.#loopable) {
        const count = this.#slides.length;
        const from = Math.round(this.#target);
        let delta = index - this.#wrapIndex(from);
        delta -= Math.round(delta / count) * count;
        this.#target = from + delta;
      } else {
        this.#target = index;
      }

      this.#run();
      this.#restartAutoplay();
    }

    #step(direction) {
      const count = this.#slides.length;
      const next = Math.round(this.#target) + direction;

      if (this.#loopable) {
        this.#target = next;
      } else {
        // Bez pętli autoplay dojeżdża do końca i wraca na początek.
        this.#target = next > count - 1 ? 0 : Math.max(next, 0);
      }

      this.#run();
    }

    #wrapIndex(value) {
      const count = this.#slides.length;
      return ((value % count) + count) % count;
    }

    #measure() {
      /* CZYTAMY STYL RAZ, NIE PRZY KAZDYM `resize`.

         `getComputedStyle` wymusza przeliczenie ukladu, a `#measure` leci
         z `ResizeObserver` — czyli takze przy chowaniu paska adresu na
         telefonie i przy obrocie ekranu. Zmierzone w sladzie wydajnosci
         na produkcji: 104 ms na `sleek-hero.js`, najwiecej sposrod naszych
         skryptow.

         Z czterech odczytow tylko JEDEN naprawde zalezy od rozmiaru —
         szerokosc slajdu. Odstep, krzywa wygaszania i prog przeciagniecia
         to stale tokeny skorki: nie zmieniaja sie z szerokoscia okna, wiec
         czytamy je raz i trzymamy. */
      if (!this.#stylesRead) {
        const styles = getComputedStyle(this);
        this.#gap = parseFloat(styles.getPropertyValue('--sleek-hero-gap')) || 0;
        this.#tau = parseFloat(styles.getPropertyValue('--sleek-hero-ease-tau')) || 110;
        this.#threshold = parseFloat(styles.getPropertyValue('--sleek-hero-drag-threshold')) || 8;
        this.#stylesRead = true;
      }

      const slideWidth = this.#slides[0].offsetWidth;
      this.#stride = slideWidth + this.#gap;

      // Pętla działa tylko wtedy, gdy tor jest dość długi, żeby ukryć przeskok.
      // Slajd zawija się na granicy N/2 skoku od środka, a znika dopiero za
      // (okno + slajd) / 2 — jeśli granica wypada bliżej, przeskok byłoby widać.
      //
      // Trzy slajdy Sleeka mieszczą się z zapasem ~285-400px na każdym progu.
      // Dwa już nie: granica leży na 1 skoku, a slajd znika dopiero za 1.2.
      // Wtedy zamiast udawać pętlę jedziemy zwykłym zakresem od pierwszego do
      // ostatniego — lepsze niż skok w środku kadru.
      this.#loopable = (this.#slides.length / 2) * this.#stride > (this.clientWidth + slideWidth) / 2;
    }

    /** Rozstawia slajdy — jedyne miejsce, w którym cokolwiek zapisujemy do DOM-u. */
    #paint() {
      const count = this.#slides.length;

      for (let index = 0; index < count; index += 1) {
        let delta = index - this.#pos;
        // najbliższa strona: to tutaj dzieje się pętla
        if (this.#loopable) delta -= Math.round(delta / count) * count;
        this.#slides[index].style.setProperty('--sleek-hero-x', `${delta * this.#stride}px`);
      }

      this.#syncActive();
    }

    #syncActive() {
      const active = this.#wrapIndex(Math.round(this.#pos));
      if (active === this.#active) return;
      this.#active = active;

      // Horizon wpisuje aria-hidden raz, przy renderowaniu — po pętli
      // wskazywałoby wciąż na slajd pierwszy. Od tego atrybutu zależy też
      // `content-visibility` i `cursor` w slideshow-styles.liquid.
      this.#slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', index === active ? 'false' : 'true');
      });

      this.#replayEntrance(active);

      this.#dots.forEach((dot, index) => {
        const current = index === active;
        dot.setAttribute('aria-current', current ? 'true' : 'false');

        // Restart animacji wypełnienia: bez wymuszenia przeliczenia układu
        // przeglądarka uzna, że nic się nie zmieniło, i pasek nie ruszy od zera.
        if (!current) return;
        const fill = dot.firstElementChild;
        if (!(fill instanceof HTMLElement)) return;
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      });
    }

    /**
     * Wejscie slajdu — odpowiednik <motion-element> Sleeka.
     *
     * Sleek trzyma slajdy poza kadrem w stanie poczatkowym (atrybut `hold`)
     * i zdejmuje go, gdy slajd staje sie aktywny; dzieki temu animacja gra
     * przy KAZDYM przejsciu, a nie tylko raz. U nas ten stan opisuje atrybut
     * `entering` na slajdzie, a caly ruch siedzi w CSS-ie.
     *
     * Restart wymaga wymuszenia przeliczenia ukladu miedzy zdjeciem
     * a nadaniem atrybutu — inaczej przegladarka sklei obie zmiany w jedna
     * klatke, uzna ze nic sie nie stalo i animacja nie ruszy od poczatku.
     * Ten sam chwyt co przy wypelnieniu kropki wyzej.
     */
    #replayEntrance(active) {
      // Atrybut zdejmujemy ze WSZYSTKICH slajdow, nie tylko z wchodzacego.
      // Inaczej narasta: kazdy odwiedzony slajd zostaje z `entering` na stale,
      // wiec przy nastepnym podejsciu nie ma czego restartowac i animacja
      // gra tylko raz, przy pierwszym pokazaniu.
      this.#slides.forEach((slide) => slide.removeAttribute('entering'));

      if (reducedMotion.matches) return;

      const slide = this.#slides[active];
      if (!slide) return;

      void slide.offsetWidth;
      slide.setAttribute('entering', '');
    }

    #run() {
      if (this.#frame) return;
      this.#lastFrame = performance.now();
      this.#frame = requestAnimationFrame(this.#animate);
    }

    #animate = (now) => {
      const elapsed = Math.min(now - this.#lastFrame, MAX_FRAME_MS);
      this.#lastFrame = now;

      const distance = this.#target - this.#pos;

      if (Math.abs(distance) < SETTLED) {
        this.#pos = this.#target;
        this.#normalize();
        this.#paint();
        this.#frame = 0;
        return;
      }

      // Dochodzenie wykładnicze, całkowane po czasie — ten sam przebieg
      // niezależnie od tego, czy ekran ma 60, czy 144 Hz.
      this.#pos += distance * (1 - Math.exp(-elapsed / this.#tau));
      this.#paint();
      this.#frame = requestAnimationFrame(this.#animate);
    };

    /**
     * Ściąga pozycję z powrotem w okolice zera. Przesunięcie o wielokrotność
     * liczby slajdów nie zmienia ani jednego transformu (patrz #paint), a bez
     * tego `pos` rosłoby w nieskończoność i po długim autoplayu zaczęłoby
     * gubić precyzję.
     */
    #normalize() {
      if (!this.#loopable) return;
      const wrapped = this.#wrapIndex(this.#pos);
      this.#target += wrapped - this.#pos;
      this.#pos = wrapped;
    }

    // ── autoplay ───────────────────────────────────────────────────────────

    #restartAutoplay() {
      clearTimeout(this.#timer);
      if (!this.#delay || reducedMotion.matches) return;
      this.#timer = setTimeout(this.#advance, this.#delay);
    }

    /**
     * Horizon pomijał tyknięcie na `:hover`. Na hero zajmującym większość okna
     * kursor leży na nim niemal zawsze, więc autoplay nigdy nie ruszał. Sleek
     * też nie zatrzymuje się pod kursorem — wstrzymuje go tylko schowana karta
     * i trwający gest.
     */
    #advance = () => {
      if (document.hidden || this.#drag) {
        this.#restartAutoplay();
        return;
      }

      this.#step(1);
      this.#restartAutoplay();
    };

    // ── przeciąganie ───────────────────────────────────────────────────────

    #onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.#suppressClick = false;

      // Gestu NIE przejmujemy tutaj. Dopóki palec nie przejedzie progu w
      // poziomie, zdarzenie należy do przeglądarki: tekst się zaznacza, linki
      // klikają, strona przewija w pionie. Dopiero próg robi z tego karuzelę.
      this.#drag = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: event.clientX,
        from: this.#pos,
        active: false,
        moved: 0,
        time: event.timeStamp,
      };

      this.addEventListener('pointermove', this.#onPointerMove);
      this.addEventListener('pointerup', this.#onPointerUp);
      this.addEventListener('pointercancel', this.#onPointerUp);
    };

    #onPointerMove = (event) => {
      const drag = this.#drag;
      if (!drag || event.pointerId !== drag.id) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (!drag.active) {
        // Pion wygrał — oddajemy gest przeglądarce i już go nie odbieramy.
        if (Math.abs(dy) > this.#threshold && Math.abs(dy) >= Math.abs(dx)) {
          this.#endDrag();
          return;
        }
        if (Math.abs(dx) <= this.#threshold) return;

        drag.active = true;
        // Liczymy od progu, nie od punktu dotknięcia — inaczej tor skoczyłby
        // o te kilka pikseli w momencie przejęcia gestu.
        drag.originX = event.clientX;
        drag.from = this.#pos;
        drag.time = event.timeStamp;

        this.setPointerCapture(drag.id);
        this.setAttribute('dragging', '');
        clearTimeout(this.#timer);
        cancelAnimationFrame(this.#frame);
        this.#frame = 0;
        return;
      }

      drag.moved = event.clientX - drag.originX;
      const next = drag.from - drag.moved / this.#stride;
      // Bez pętli nie ma dokąd wyjechać poza skrajne slajdy — byłaby tam pustka.
      this.#pos = this.#loopable ? next : Math.min(Math.max(next, 0), this.#slides.length - 1);
      this.#target = this.#pos;
      this.#paint();
    };

    #onPointerUp = (event) => {
      const drag = this.#drag;
      if (!drag || event.pointerId !== drag.id) return;

      if (drag.active) {
        const elapsed = Math.max(event.timeStamp - drag.time, 1);
        const velocity = drag.moved / elapsed;
        // Przesunięcie w jednostkach slajdu, dodatnie w stronę kolejnego.
        const moved = -drag.moved / this.#stride;
        const flick = Math.abs(velocity) > FLICK_VELOCITY;

        // Zwykłe puszczenie przyciąga do najbliższego slajdu. Rzut zawsze
        // przenosi o co najmniej jeden, w stronę gestu.
        const steps = flick
          ? Math.sign(moved) * Math.max(1, Math.round(Math.abs(moved)))
          : Math.round(moved);

        const landed = Math.round(drag.from) + steps;
        this.#target = this.#loopable
          ? landed
          : Math.min(Math.max(landed, 0), this.#slides.length - 1);
        this.#suppressClick = Math.abs(drag.moved) > this.#threshold;

        if (this.hasPointerCapture(drag.id)) this.releasePointerCapture(drag.id);
        this.removeAttribute('dragging');
        this.#run();
      }

      this.#endDrag();
      this.#restartAutoplay();
    };

    #endDrag() {
      this.#drag = null;
      this.removeEventListener('pointermove', this.#onPointerMove);
      this.removeEventListener('pointerup', this.#onPointerUp);
      this.removeEventListener('pointercancel', this.#onPointerUp);
    }

    /**
     * Przeciągnięcie nie ma kończyć się wejściem w link ze slajdu.
     * Samo kliknięcie w wystającego sąsiada przewija na niego — Horizon
     * zapowiada to kursorem (`slideshow-slide[aria-hidden='true']`).
     */
    #onClick = (event) => {
      if (this.#suppressClick) {
        this.#suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const target = /** @type {HTMLElement} */ (event.target);
      const index = this.#slides.findIndex((slide) => slide.contains(target));
      if (index < 0 || index === this.#active) return;

      event.preventDefault();
      event.stopPropagation();
      this.goTo(index);
    };

    /** Edytor motywu: zaznaczenie slajdu w panelu ma go pokazać na stronie. */
    #onBlockSelect = (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const index = this.#slides.findIndex((slide) => slide === target || slide.contains(target));
      if (index >= 0) this.goTo(index);
    };
  }

  if (!customElements.get('sleek-hero')) customElements.define('sleek-hero', SleekHero);
})();
